import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, AlertCircle, CheckCircle2, XCircle, Award, Users,
  MapPin, Calendar, Building2, Search, X, ShieldCheck, ExternalLink, Clock,
  AlertTriangle,
} from "lucide-react";
import {
  useAdminGetChapterEventRequestQuery,
  useAdminApproveChapterEventRequestMutation,
  useAdminRejectChapterEventRequestMutation,
  useAdminAllocateChapterEventCpdMutation,
  type ChapterEventAttendee,
  type ChapterEventRequestDetail,
} from "../../chapter/api/chapterEventsApi";
import ChapterEventStatusBadge from "../../chapter/components/ChapterEventStatusBadge";

/**
 * One chapter event request, as the secretariat decides it.
 *
 * Two decisions live here, and only one of them is ever available:
 *
 *   while `pending`               approve (at the asked-for points or fewer) or
 *                                 reject with a reason
 *   while `attendance_submitted`  allocate — which CREATES the chapter event,
 *                                 records the whole register as present, and
 *                                 credits every member on it
 *
 * The allocate button is the consequential one, so it says what it will do
 * before it is pressed: how many members will be credited, at how many points,
 * and how many names will be recorded without CPD.
 */

function safeMsg(e: any): string {
  const errors = e?.data?.errors;
  if (errors) return Object.values(errors).flat().join(" ");
  return e?.data?.message || e?.error || e?.message || "Something went wrong.";
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function fmtDateTime(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T")).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// ── approve ──────────────────────────────────────────────────────────────────

function ApproveModal({
  req,
  onClose,
  onDone,
}: {
  req: ChapterEventRequestDetail;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  // Pre-filled with the ask, so approving it unchanged is one press and cutting
  // it is an edit — which is the right way round for a figure the chapter chose.
  const [points, setPoints] = useState(String(req.proposed_cpd_points));
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [approve, { isLoading }] = useAdminApproveChapterEventRequestMutation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res: any = await approve({
        id: req.id,
        approved_cpd_points: points,
        notes: notes || undefined,
      }).unwrap();
      onDone(res?.message || "Approved.");
    } catch (err) {
      setError(safeMsg(err));
    }
  };

  const reduced = Number(points) < Number(req.proposed_cpd_points);

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <form onSubmit={submit} className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[94vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-gray-900">Approve event request</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!!error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">
              CPD points to allow
            </label>
            <input
              type="number"
              step="0.5"
              min="0.5"
              max={req.proposed_cpd_points}
              required
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
            <p className="text-[10px] text-gray-400">
              {req.chapter?.name} asked for {req.proposed_cpd_points}. You may allow fewer, not more.
            </p>
            {reduced && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                Reducing to {points} point(s). The chapter sees the reduction on its own screen.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">
              Note to the chapter
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={reduced ? "Why the points were reduced." : "Optional."}
              className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 py-2.5 bg-green-600 rounded-lg text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Approve
          </button>
        </div>
      </form>
    </div>
  );
}

// ── reject ───────────────────────────────────────────────────────────────────

function RejectModal({
  req,
  onClose,
  onDone,
}: {
  req: ChapterEventRequestDetail;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [reject, { isLoading }] = useAdminRejectChapterEventRequestMutation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res: any = await reject({ id: req.id, reason }).unwrap();
      onDone(res?.message || "Rejected.");
    } catch (err) {
      setError(safeMsg(err));
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <form onSubmit={submit} className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-gray-900">Reject event request</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!!error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">Reason *</label>
            <textarea
              rows={4}
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What the chapter needs to change."
              className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
            {/* The reason is required because a rejected request can be amended
                and re-submitted — without one it comes back unchanged. */}
            <p className="text-[10px] text-gray-400">
              The chapter can amend the request and re-submit it, so say what is wrong.
            </p>
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 py-2.5 bg-red-600 rounded-lg text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Reject
          </button>
        </div>
      </form>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function AdminChapterEventRequestDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const { data: req, isLoading, error: loadError } = useAdminGetChapterEventRequestQuery(id, { skip: !id });
  const [allocate, { isLoading: allocating }] = useAdminAllocateChapterEventCpdMutation();

  const [ok, setOk] = useState("");
  const [error, setError] = useState("");
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [filter, setFilter] = useState("");

  // Memoised, not just `req?.attendees ?? []`: the fallback is a fresh array on
  // every render, which would make the filter below re-run each time.
  const attendees: ChapterEventAttendee[] = useMemo(() => req?.attendees ?? [], [req]);
  const memberCount = attendees.filter((a) => a.is_member).length;
  const unmatchedCount = attendees.filter((a) => a.needs_match).length;
  // What a press would actually pay out: matched, and not already credited. On a
  // first allocation that is every member; on a re-run it is only the rows the
  // chapter has since corrected.
  const toCredit = attendees.filter((a) => a.is_member && !a.cpd_issued).length;
  const alreadyCredited = attendees.filter((a) => a.cpd_issued).length;

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return attendees;
    return attendees.filter((a) =>
      [a.name, a.membership_no, a.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [attendees, filter]);

  const runAllocate = async () => {
    if (!req) return;

    // A top-up reads quite differently from the first allocation, so it is
    // spelled out differently: nothing is created, nothing already credited is
    // touched, and only the corrected rows are paid.
    const topUp = req.status === "allocated";

    const confirmation = topUp
      ? `Credit ${req.effective_cpd_points} CPD point(s) to the ${toCredit} attendee(s) matched since the last allocation?\n\n` +
        `The ${alreadyCredited} already credited are not touched, and no new event is created.`
      : `Allocate ${req.effective_cpd_points} CPD point(s) to ${toCredit} member(s)?\n\n` +
        `This creates "${req.title}" as a ${req.chapter?.name ?? ""} chapter event, records all ` +
        `${attendees.length} attendee(s) as present, and credits the members' CPD records. It cannot be undone from here.`;

    if (!window.confirm(confirmation)) return;

    setError(""); setOk("");
    try {
      const res: any = await allocate(id).unwrap();
      setOk(res?.message || "CPD allocated.");
    } catch (e) {
      setError(safeMsg(e));
    }
  };

  if (isLoading) {
    return (
      <div className="p-10 flex flex-col items-center gap-2 text-gray-400 bg-white rounded-lg shadow-sm">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-xs font-medium">Loading request…</span>
      </div>
    );
  }

  if (loadError || !req) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm space-y-4">
        <div className="px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm font-semibold text-red-700">
          {(loadError as any)?.data?.message || "This request could not be loaded."}
        </div>
        <button
          onClick={() => navigate("/admin/chapter-event-requests")}
          className="text-sm font-semibold text-gray-600 underline"
        >
          Back to chapter event requests
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white rounded-lg shadow-sm">
      <Link
        to="/admin/chapter-event-requests"
        className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-900 uppercase tracking-wide"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Chapter event requests
      </Link>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{req.title}</h2>
          <ChapterEventStatusBadge status={req.status} />
        </div>
        {!!req.theme && <p className="text-sm text-gray-500 italic">{req.theme}</p>}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1 font-semibold text-gray-700">
            <Building2 className="w-3.5 h-3.5" /> {req.chapter?.name ?? "—"}
          </span>
          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {req.location}</span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {fmtDate(req.start_date)}{req.end_date && req.end_date !== req.start_date ? ` – ${fmtDate(req.end_date)}` : ""}
          </span>
          {!!req.requested_by && (
            <span className="text-gray-400">
              Requested by {req.requested_by}
              {req.requested_by_email ? ` (${req.requested_by_email})` : ""}
            </span>
          )}
        </div>
      </div>

      {!!ok && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-green-200 bg-green-50 text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{ok}</span>
        </div>
      )}
      {!!error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="border rounded-2xl p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">Requested</div>
          <div className="text-2xl font-bold text-gray-900">{req.proposed_cpd_points}</div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mt-0.5">CPD points</div>
        </div>
        <div className="border rounded-2xl p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">Approved</div>
          <div className={`text-2xl font-bold ${req.cpd_was_reduced ? "text-amber-600" : "text-gray-900"}`}>
            {req.approved_cpd_points ?? "—"}
          </div>
          {req.cpd_was_reduced && (
            <div className="text-[10px] font-bold text-amber-600 uppercase tracking-wide mt-0.5">Reduced</div>
          )}
        </div>
        <div className="border rounded-2xl p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">On register</div>
          <div className="text-2xl font-bold text-gray-900">{attendees.length}</div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mt-0.5">
            {memberCount} member{memberCount === 1 ? "" : "s"}
            {unmatchedCount > 0 ? ` · ${unmatchedCount} unmatched` : ""}
          </div>
        </div>
        <div className={`border rounded-2xl p-4 ${toCredit > 0 ? "border-gray-900" : ""}`}>
          {/* What THIS press would pay out, not what the register is worth in
              total — on a re-run those are very different numbers, and the one
              that matters is what is about to be issued. */}
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
            {toCredit > 0 ? "Points to issue now" : "Points issued"}
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {toCredit > 0
              ? Math.round(toCredit * req.effective_cpd_points * 100) / 100
              : (req.allocation?.total_points ?? 0)}
          </div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mt-0.5">
            {toCredit > 0
              ? `${req.effective_cpd_points} × ${toCredit}`
              : `${alreadyCredited} credited`}
          </div>
        </div>
      </div>

      {unmatchedCount > 0 && (
        // The secretariat cannot fix these — only the chapter can, from its own
        // register — so this says whose move it is rather than offering a control.
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border-2 border-amber-300 bg-amber-50 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <span className="font-bold">
              {unmatchedCount} attendee{unmatchedCount === 1 ? "" : "s"} on this register match no member
            </span>{" "}
            and cannot be credited. The chapter can correct the membership numbers on
            those rows at any time — including now — and this page will offer the
            allocation again for just them.
          </span>
        </div>
      )}

      {!!req.details && (
        <div className="border rounded-2xl p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
            Details from the chapter
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{req.details}</p>
        </div>
      )}

      {!!req.review_notes && (
        <div
          className={`px-3 py-2.5 rounded-lg border text-sm ${
            req.status === "rejected"
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-blue-200 bg-blue-50 text-blue-800"
          }`}
        >
          <span className="font-bold">
            {req.status === "rejected" ? "Rejected" : "Review note"}
            {req.reviewed_by ? ` — ${req.reviewed_by}` : ""}
            {req.reviewed_at ? ` · ${fmtDateTime(req.reviewed_at)}` : ""}:
          </span>{" "}
          {req.review_notes}
        </div>
      )}

      {/* ── the decision ───────────────────────────────────────────────── */}

      {req.can_review && (
        <div className="border-2 border-gray-900 rounded-2xl p-4 space-y-3">
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Decision</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Approving lets the chapter upload its attendance. Nothing is credited
              to anyone yet.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => { setShowApprove(true); setOk(""); setError(""); }}
              className="flex-1 py-2.5 bg-green-600 rounded-lg text-sm font-semibold text-white hover:bg-green-700 inline-flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
            <button
              onClick={() => { setShowReject(true); setOk(""); setError(""); }}
              className="flex-1 py-2.5 border border-red-300 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 inline-flex items-center justify-center gap-2"
            >
              <XCircle className="w-4 h-4" /> Reject
            </button>
          </div>
        </div>
      )}

      {req.status === "approved" && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-800">
          <Clock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Approved at {req.effective_cpd_points} point(s). Waiting on the chapter to
            upload its attendance and submit it for allocation.
          </span>
        </div>
      )}

      {req.can_allocate && (
        <div className="border-2 border-gray-900 rounded-2xl p-4 space-y-3">
          <div>
            <h3 className="font-bold text-gray-900 text-sm">
              {req.status === "allocated" ? "Allocate CPD to the remaining attendees" : "Allocate CPD"}
            </h3>
            {req.status === "allocated" ? (
              // A top-up. Nothing is created and nothing already paid is touched,
              // which is the reassurance this panel has to give before it is
              // pressed a second time.
              <p className="text-xs text-gray-500 mt-0.5">
                Credits {toCredit} attendee(s) matched since the last allocation with{" "}
                {req.effective_cpd_points} point(s) each. The {alreadyCredited} already
                credited are left untouched, and no new event is created — they join the
                existing <span className="font-semibold">{req.title}</span>.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-0.5">
                Creates <span className="font-semibold">{req.title}</span> as a{" "}
                {req.chapter?.name} chapter event, records all {attendees.length} attendee(s)
                as present, and credits {toCredit} member(s) with{" "}
                {req.effective_cpd_points} point(s) each.
                {unmatchedCount > 0 &&
                  ` The ${unmatchedCount} attendee(s) matching no member are recorded without CPD, and can be credited later once the chapter corrects them.`}
              </p>
            )}
            {!!req.attendance_submitted_at && req.status !== "allocated" && (
              <p className="text-[11px] text-gray-400 mt-1">
                Register submitted {fmtDateTime(req.attendance_submitted_at)}.
              </p>
            )}
          </div>
          <button
            onClick={runAllocate}
            disabled={allocating || toCredit === 0}
            className="w-full py-3 bg-gray-900 rounded-lg text-sm font-bold text-white hover:bg-gray-800 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {allocating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
            {req.status === "allocated" ? `Allocate to ${toCredit} attendee(s)` : "Allocate CPD"}
          </button>
        </div>
      )}

      {req.status === "allocated" && !!req.allocation && (
        <div className="border-2 border-green-200 bg-green-50 rounded-2xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-green-700" />
            <h3 className="font-bold text-green-800 text-sm">CPD allocated</h3>
          </div>
          <p className="text-xs text-green-800/90">
            {req.allocation.credited} member(s) credited with {req.allocation.points_each} point(s)
            each ({req.allocation.total_points} in total)
            {req.allocation.not_credited > 0
              ? `, ${req.allocation.not_credited} attendee(s) recorded without CPD`
              : ""}
            . Allocated {fmtDateTime(req.allocated_at)}
            {req.allocated_by ? ` by ${req.allocated_by}` : ""}.
          </p>
          {!!req.event_id && (
            <Link
              to={`/admin/events/${req.event_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-green-300 text-green-800 text-[11px] font-bold uppercase tracking-wide hover:bg-green-100"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open the chapter event
            </Link>
          )}
        </div>
      )}

      {/* ── the register ───────────────────────────────────────────────── */}
      {attendees.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h3 className="font-bold text-gray-900 text-sm inline-flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" /> Attendance register
              <span className="text-gray-400 font-medium">({attendees.length})</span>
            </h3>
            {attendees.length > 5 && (
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter the register…"
                  className="border pl-8 pr-3 py-2 rounded-lg text-xs outline-none focus:border-blue-500"
                />
              </div>
            )}
          </div>

          <div className="border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                    <th className="px-3 py-2.5">Name</th>
                    <th className="px-3 py-2.5">Member no</th>
                    <th className="px-3 py-2.5 hidden sm:table-cell">Email</th>
                    <th className="px-3 py-2.5 hidden md:table-cell">Added</th>
                    <th className="px-3 py-2.5">CPD</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((a) => (
                    <tr
                      key={a.id}
                      className={`border-t ${a.needs_match ? "bg-amber-50/60" : ""}`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-gray-900">{a.name}</div>
                        {a.needs_match && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 mt-0.5">
                            <AlertTriangle className="w-3 h-3" /> No member matched
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500">{a.membership_no || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-500 hidden sm:table-cell">{a.email || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-400 hidden md:table-cell capitalize">
                        {a.source === "import" ? "Spreadsheet" : "By hand"}
                      </td>
                      <td className="px-3 py-2.5">
                        {a.cpd_issued ? (
                          <span className="inline-flex items-center gap-1 text-green-700 font-bold text-xs">
                            <Award className="w-3.5 h-3.5" /> {a.cpd_points}
                          </span>
                        ) : a.needs_match ? (
                          <span className="text-xs font-semibold text-amber-700">
                            {req.status === "allocated"
                              ? "Not credited — chapter to fix"
                              : "Matches nobody"}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Will earn CPD</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showApprove && (
        <ApproveModal
          req={req}
          onClose={() => setShowApprove(false)}
          onDone={(m) => { setShowApprove(false); setOk(m); }}
        />
      )}
      {showReject && (
        <RejectModal
          req={req}
          onClose={() => setShowReject(false)}
          onDone={(m) => { setShowReject(false); setOk(m); }}
        />
      )}
    </div>
  );
}
